import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DescuentoDeLinea, DescuentoDeVenta, LineaMostrador, Producto } from '@tanteo/compartido';
import {
  CANTIDAD_MAXIMA,
  desglosarVenta,
  CONSUMIDOR_FINAL,
  esCodigoValido,
  fechaNegocio,
  formatearCOP,
  horaNegocio,
  normalizarSku,
} from '@tanteo/compartido';
import { listarCuentas, listarImpuestos, type Cuenta } from '../../lib/api-cuentas';
import { listarDescuentosNegocio, type DescuentoDelNegocio } from '../../lib/api-descuentos';
import { indexarProductos, listarProductos, preciosVigentes } from '../../lib/api';
import { registrarOEncolar } from '../../lib/cola-offline';
import type { PagoDeVenta } from '../../lib/api';
import { rastrearFallo } from '../../lib/rastro';
import { guardarVenta, leerVenta, olvidarVenta, type VentaEnCurso } from '../../lib/venta-en-curso';
import {
  borrarPedido,
  crearPedido,
  guardarPedido,
  guardarPedidoEnDisco,
  listarPedidos,
  pedirPersistencia,
  siguienteNumero,
  tomarPedido,
  type Pedido,
} from '../../lib/pedidos';
import { useApp } from '../../lib/contexto';
import { Boton, ErrorDeLectura, Esqueleto, FilaAcciones, GrupoOpciones, MensajeError, Tarjeta } from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { BotonesRapidos } from './BotonesRapidos';
import { Cambio } from './Cambio';
import { DescuentoProducto } from './DescuentoProducto';
import { DescuentoVenta } from './DescuentoVenta';
import { PagoMixto } from './PagoMixto';
import { PanelLineas } from './PanelLineas';
import { SelectorCliente } from './SelectorCliente';
import { EntradaEscaneo, type EntradaEscaneoHandle } from './EntradaEscaneo';
import { FranjaPedidos } from './FranjaPedidos';
import { NuevoPedido } from './NuevoPedido';
import { aplicarPromociones } from './promocionesEnVenta';

type Resultado = { tipo: 'ok'; consecutivo: string; total: number; ventaId: string } | { tipo: 'pendiente'; total: number };

export function Mostrador() {
  const { negocio } = useApp();
  const [productos, setProductos] = useState<Producto[] | null>(null);
  const [lineas, setLineas] = useState<LineaMostrador[]>([]);
  const [cuentas, setCuentas] = useState<Cuenta[]>([]);
  const [cuenta, setCuenta] = useState<Cuenta | null>(null);
  const [aviso, setAviso] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [cliente, setCliente] = useState<{ id: string; nombre: string; numeroDocumento: string } | null>(null);
  /**
   * Identificador de idempotencia de ESTA venta: nace con la primera línea y vive hasta cerrarla.
   * Un reintento de Confirmar lleva el MISMO uuid; con uno nuevo el cliente pagaría dos veces.
   */
  const [idVenta, setIdVenta] = useState<string | null>(null);
  const [eligiendoCliente, setEligiendoCliente] = useState(false);
  const [recuperada, setRecuperada] = useState(false);
  /** Reparto de un cobro mixto. Con esto puesto, el paso 3 muestra el desglose y no una cuenta. */
  const [pagos, setPagos] = useState<PagoDeVenta[] | null>(null);
  const [calculandoCambio, setCalculandoCambio] = useState(false);
  const [repartiendo, setRepartiendo] = useState(false);
  const [descuentoVenta, setDescuentoVenta] = useState<DescuentoDeVenta | null>(null);
  /** El producto cuyo descuento se está editando. */
  const [skuDescuento, setSkuDescuento] = useState<string | null>(null);
  const [descontandoVenta, setDescontandoVenta] = useState(false);
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  /** El pedido en pantalla; null es MOSTRADOR, la venta suelta. */
  const [pedidoActivo, setPedidoActivo] = useState<Pedido | null>(null);
  const [guardadoPedido, setGuardadoPedido] = useState<string | null>(null);
  const [creandoPedido, setCreandoPedido] = useState(false);
  /** Suelta el pedido en pantalla para que otra pestaña pueda abrirlo. */
  const soltarPedido = useRef<(() => void) | null>(null);
  const [descuentos, setDescuentos] = useState<DescuentoDelNegocio[]>([]);
  /** Cuántos impuestos activos tiene el negocio: sin ninguno no se muestran columna ni bloque de impuestos. */
  const [impuestosActivos, setImpuestosActivos] = useState(0);
  /** Se pidió el descuento a la venta con descuentos en productos: se dice por qué no se puede. */
  const [avisoVentaBloqueada, setAvisoVentaBloqueada] = useState(false);
  // Lo que no se pudo leer al abrir el mostrador (A4): un fallo no se muestra como lista vacía.
  const [errorCuentas, setErrorCuentas] = useState<unknown>(null);
  const [errorDescuentos, setErrorDescuentos] = useState<unknown>(null);
  const [errorImpuestos, setErrorImpuestos] = useState<unknown>(null);
  const [errorPedidos, setErrorPedidos] = useState<unknown>(null);
  /** Por qué se reabrió el reparto al cobrar, cuando el total cambió desde que se repartió (A5). */
  const [avisoReparto, setAvisoReparto] = useState<string | null>(null);
  /** Los precios que cambiaron desde que se cargó la venta, para el diálogo de confirmación (A5). */
  const [preciosCambiados, setPreciosCambiados] = useState<{ nombre: string; antes: number; ahora: number }[]>([]);
  const abriendoConfirmacion = useRef(false);
  const scan = useRef<EntradaEscaneoHandle>(null);
  /** La cuenta del borrador se guarda por id y se resuelve cuando llegan las cuentas. */
  const cuentaDelBorrador = useRef<string | null>(null);
  const escapes = useRef(0);
  /** Las líneas vigentes para `agregarProducto`, que no se recrea con cada escaneo. */
  const lineasActuales = useRef<LineaMostrador[]>([]);
  useEffect(() => {
    lineasActuales.current = lineas;
  }, [lineas]);
  const pedidosActuales = useRef<Pedido[]>([]);
  const pedidoEnPantalla = useRef<Pedido | null>(null);
  useEffect(() => {
    pedidosActuales.current = pedidos;
    pedidoEnPantalla.current = pedidoActivo;
  }, [pedidos, pedidoActivo]);
  const hayDialogo =
    confirmando ||
    calculandoCambio ||
    repartiendo ||
    eligiendoCliente ||
    skuDescuento !== null ||
    descontandoVenta ||
    creandoPedido;
  // Solo los descuentos manuales activos que el dueño marcó para este rol; sin ninguno no aparecen sus botones.
  // Las promociones no se eligen: se ponen solas en las líneas que cubren.
  const descuentosMios = descuentos.filter(
    (d) =>
      d.activo &&
      !d.automatico &&
      ((negocio?.rol === 'propietario' && d.paraPropietario) || (negocio?.rol === 'cajero' && d.paraCajero))
  );
  const promociones = useMemo(() => descuentos.filter((d) => d.automatico && d.activo), [descuentos]);
  const descuentosDeProducto = descuentosMios.filter((d) => d.enProducto);
  const descuentosDeVenta = descuentosMios.filter((d) => d.enVenta);
  const puedeDescontarVenta = descuentosDeVenta.length > 0;
  // Un solo tipo de descuento por venta, con la regla de `registrar_venta`: un manual en algún producto, o una
  // rebaja real en productos, cierra el descuento a la venta. Un automático que no rebaja nada no lo cierra.
  const hayDescuentoEnProductos =
    lineas.some((l) => l.descuento && !l.descuento.automatico) || desglosarVenta(lineas).descuentoLineas > 0;

  // Al cerrarse cualquier diálogo el foco vuelve al escáner: la próxima lectura tiene dónde caer.
  useEffect(() => {
    if (!hayDialogo) scan.current?.enfocar();
  }, [hayDialogo]);

  const cargarCuentas = useCallback(() => {
    if (!negocio) return;
    setErrorCuentas(null);
    listarCuentas(negocio.negocioId)
      .then((cs) => {
        setCuentas(cs);
        const id = cuentaDelBorrador.current;
        cuentaDelBorrador.current = null;
        if (id) setCuenta(cs.find((c) => c.id === id) ?? null);
      })
      .catch((e: unknown) => {
        setCuentas([]);
        setErrorCuentas(e);
      });
  }, [negocio]);
  const cargarDescuentos = useCallback(() => {
    if (!negocio) return;
    setErrorDescuentos(null);
    listarDescuentosNegocio(negocio.negocioId)
      .then(setDescuentos)
      .catch((e: unknown) => {
        setDescuentos([]);
        setErrorDescuentos(e);
      });
  }, [negocio]);
  const cargarImpuestos = useCallback(() => {
    if (!negocio) return;
    setErrorImpuestos(null);
    listarImpuestos(negocio.negocioId)
      .then((is) => setImpuestosActivos(is.length))
      .catch((e: unknown) => {
        setImpuestosActivos(0);
        setErrorImpuestos(e);
      });
  }, [negocio]);
  const cargarPedidos = useCallback(() => {
    if (!negocio) return;
    setErrorPedidos(null);
    listarPedidos(negocio.negocioId)
      .then(setPedidos)
      .catch((e: unknown) => setErrorPedidos(e));
  }, [negocio]);

  useEffect(() => {
    if (!negocio) return;
    setProductos(null);
    listarProductos(negocio.negocioId)
      .then(setProductos)
      .catch(() => {
        setProductos([]);
        setAviso('No pudimos cargar el catálogo. Revisa la conexión.');
      });
    cargarCuentas();
    cargarDescuentos();
    cargarPedidos();
    cargarImpuestos();
  }, [negocio, cargarCuentas, cargarDescuentos, cargarPedidos, cargarImpuestos]);

  // Al salir del mostrador se suelta el pedido en pantalla: el candado solo se libera así, y sin esto
  // la propia pestaña lo encuentra tomado al volver.
  useEffect(() => () => soltarPedido.current?.(), []);

  // La venta que quedó a medias en este equipo, para que un refresco no cueste volver a escanear.
  // Se lee una vez al abrir el mostrador; el módulo descarta lo de otro negocio y lo de otro día.
  useEffect(() => {
    if (!negocio) return;
    const borrador = leerVenta(negocio.negocioId, fechaNegocio());
    if (!borrador) return;
    setLineas(borrador.lineas);
    setCliente(borrador.cliente);
    setIdVenta(borrador.idVenta);
    setPagos(borrador.pagos);
    setDescuentoVenta(borrador.descuento ?? null);
    cuentaDelBorrador.current = borrador.cuentaId;
    setRecuperada(true);
  }, [negocio]);

  // Y se guarda a cada cambio. Sin líneas no hay borrador: la clave se borra en vez de guardar uno
  // vacío que reaparecería encima de la venta siguiente.
  useEffect(() => {
    if (!negocio) return;
    const venta: VentaEnCurso | null =
      lineas.length > 0 && idVenta
        ? { idVenta, lineas, cliente, cuentaId: cuenta?.id ?? null, pagos, descuento: descuentoVenta }
        : null;
    if (pedidoActivo) {
      if (!venta) return;
      const actualizado = { ...pedidoActivo, venta, guardadoEn: Date.now() };
      const ok = guardarPedido(actualizado);
      setGuardadoPedido(ok ? `guardado ${horaNegocio().slice(0, 5)}` : 'No se pudo guardar en este equipo');
      setPedidos((ps) => ps.map((p) => (p.id === actualizado.id ? actualizado : p)));
      return;
    }
    if (!venta) {
      olvidarVenta();
      return;
    }
    guardarVenta(negocio.negocioId, fechaNegocio(), venta);
  }, [negocio, lineas, cliente, cuenta, pagos, idVenta, descuentoVenta, pedidoActivo]);

  // Al ocultarse la pestaña, el pedido en pantalla también queda en el disco.
  useEffect(() => {
    if (!pedidoActivo) return;
    const volcar = () => {
      if (document.visibilityState !== 'hidden') return;
      const guardado = pedidos.find((p) => p.id === pedidoActivo.id);
      if (guardado) void guardarPedidoEnDisco(guardado);
    };
    document.addEventListener('visibilitychange', volcar);
    window.addEventListener('pagehide', volcar);
    return () => {
      document.removeEventListener('visibilitychange', volcar);
      window.removeEventListener('pagehide', volcar);
    };
  }, [pedidoActivo, pedidos]);

  // Sin productos no hay a qué descontar: la venta siguiente no hereda el descuento.
  useEffect(() => {
    if (lineas.length === 0) setDescuentoVenta(null);
  }, [lineas.length]);

  useEffect(() => {
    if (!avisoVentaBloqueada) return;
    if (!hayDescuentoEnProductos) {
      setAvisoVentaBloqueada(false);
      return;
    }
    const reloj = setTimeout(() => setAvisoVentaBloqueada(false), 5000);
    return () => clearTimeout(reloj);
  }, [avisoVentaBloqueada, hayDescuentoEnProductos]);

  /** Abre el descuento a la venta, o dice por qué no se puede si hay descuentos en productos. */
  const pedirDescuentoVenta = useCallback(() => {
    if (hayDescuentoEnProductos) setAvisoVentaBloqueada(true);
    else setDescontandoVenta(true);
  }, [hayDescuentoEnProductos]);

  const indice = useMemo(() => indexarProductos(productos ?? []), [productos]);
  const categoriaDe = useCallback((id: string) => productos?.find((p) => p.id === id)?.categoria, [productos]);
  const conDescuentoVenta = descuentoVenta !== null;

  // Cada cambio de líneas, de promociones o del descuento a la venta vuelve a poner las promociones que valen hoy.
  useEffect(() => {
    setLineas((ls) => aplicarPromociones(ls, promociones, categoriaDe, fechaNegocio(), conDescuentoVenta));
  }, [lineas, promociones, categoriaDe, conDescuentoVenta]);
  const totales = useMemo(() => desglosarVenta(lineas, descuentoVenta), [lineas, descuentoVenta]);
  // Las líneas se leen sin el descuento a la venta, que no es de ninguna: ese se ve solo abajo.
  const desgloseLineas = useMemo(() => desglosarVenta(lineas).lineas, [lineas]);
  // Descuento e impuestos se muestran si el negocio los tiene configurados, o si la venta ya trae alguno.
  const mostrarDescuento =
    descuentosMios.length > 0 || promociones.length > 0 || totales.descuentoLineas + totales.descuentoVenta > 0;
  const mostrarImpuestos = impuestosActivos > 0 || totales.impuesto > 0;
  const impuestosPorNombre = totales.lineas
    .flatMap((l) => l.impuestos)
    .reduce((m, imp) => m.set(imp.nombre, (m.get(imp.nombre) ?? 0) + imp.monto), new Map<string, number>());
  const nombresDescuento = [
    ...new Set([...lineas.flatMap((l) => (l.descuento ? [l.descuento.nombre] : [])), ...(descuentoVenta ? [descuentoVenta.nombre] : [])]),
  ];
  const conDescuento = totales.descuentoLineas + totales.descuentoVenta > 0;
  const etiquetaDescuento =
    totales.descuentoLineas > 0
      ? 'Desc. productos'
      : totales.descuentoVenta > 0 && descuentoVenta
        ? `Desc. venta${descuentoVenta.tipo === 'pct' ? ` ${descuentoVenta.valor}%` : ''}`
        : 'Descuento';
  // El reparto de un pago mixto, como texto: lo muestran el paso 3 y la confirmación.
  const resumenPagos = useMemo(
    () =>
      pagos
        ? pagos
            .map((g) => `${cuentas.find((c) => c.id === g.cuentaId)?.nombre ?? 'Cuenta'} ${formatearCOP(g.monto)}`)
            .join(' · ')
        : null,
    [pagos, cuentas]
  );

  const agregarProducto = useCallback((p: Producto) => {
    if ((lineasActuales.current.find((l) => l.productoId === p.id)?.cantidad ?? 0) >= CANTIDAD_MAXIMA) {
      setAviso(`${p.nombre} ya tiene ${CANTIDAD_MAXIMA.toLocaleString('es-CO')} unidades, el máximo de una venta.`);
      return;
    }
    setAviso('');
    setResultado(null);
    setIdVenta((previo) => previo ?? crypto.randomUUID());
    setLineas((previas) => {
      const existente = previas.find((l) => l.productoId === p.id);
      if (existente) {
        return previas.map((l) =>
          l.productoId === p.id ? { ...l, cantidad: Math.min(l.cantidad + 1, CANTIDAD_MAXIMA) } : l
        );
      }
      return [
        ...previas,
        { sku: p.sku, nombre: p.nombre, productoId: p.id, categoria: p.categoria, cantidad: 1, precioUnit: p.precio, impuestos: p.impuestos },
      ];
    });
  }, []);

  const alCodigo = useCallback(
    (crudo: string) => {
      if (!esCodigoValido(crudo)) {
        setAviso(`«${crudo.slice(0, 30)}» no parece un código. Revisa el lector.`);
        return;
      }
      const p = indice.get(normalizarSku(crudo)) ?? indice.get(crudo.trim());
      if (!p) {
        // El índice solo tiene los activos: un código desactivado existe, y crearlo de nuevo lo duplicaría.
        const apagado = productos?.find(
          (x) => !x.activo && (normalizarSku(x.sku) === normalizarSku(crudo) || x.ean?.trim() === crudo.trim())
        );
        setAviso(
          apagado
            ? `"${apagado.nombre}" está desactivado. Actívalo en Catálogo para venderlo.`
            : `No encontré el código «${normalizarSku(crudo)}». Revisa la carta o créalo en Catálogo.`
        );
        return;
      }
      agregarProducto(p);
    },
    [indice, productos, agregarProducto]
  );

  const limpiar = useCallback(() => {
    setLineas([]);
    setIdVenta(null);
    setCuenta(null);
    setPagos(null);
    setCliente(null);
    setDescuentoVenta(null);
    setPreciosCambiados([]);
    setAviso('');
    setResultado(null);
    setRecuperada(false);
    olvidarVenta();
    scan.current?.enfocar();
  }, []);

  /** Pone en pantalla una venta guardada, o una vacía. */
  const cargarVenta = useCallback(
    (v: VentaEnCurso | null) => {
      setLineas(v?.lineas ?? []);
      setIdVenta(v?.idVenta ?? null);
      setCliente(v?.cliente ?? null);
      setPagos(v?.pagos ?? null);
      setDescuentoVenta(v?.descuento ?? null);
      setCuenta(v?.cuentaId ? (cuentas.find((c) => c.id === v.cuentaId) ?? null) : null);
      setPreciosCambiados([]);
      setAviso('');
      setResultado(null);
      setRecuperada(false);
      scan.current?.enfocar();
    },
    [cuentas]
  );

  // Suelta el pedido que sale de pantalla: a disco, o descartado si quedó vacío. Va después del cambio
  // de pantalla, con su foto: así ningún escaneo cae en otra venta.
  const soltarSaliente = useCallback(
    async (saliente: Pedido | null, vacio: boolean, soltar: (() => void) | null) => {
      if (saliente && negocio) {
        if (vacio) {
          await borrarPedido(negocio.negocioId, saliente.id);
          setPedidos((ps) => ps.filter((p) => p.id !== saliente.id));
        } else {
          await guardarPedidoEnDisco(saliente);
        }
      }
      soltar?.();
    },
    [negocio]
  );

  /** La foto del pedido en pantalla, justo antes de cambiarla. */
  const fotoDelSaliente = useCallback(() => {
    const activo = pedidoEnPantalla.current;
    const saliente = activo ? (pedidosActuales.current.find((p) => p.id === activo.id) ?? activo) : null;
    const soltar = soltarPedido.current;
    soltarPedido.current = null;
    return { saliente, vacio: lineasActuales.current.length === 0, soltar };
  }, []);

  const volverAMostrador = useCallback(() => {
    if (!pedidoEnPantalla.current || !negocio) return;
    const foto = fotoDelSaliente();
    setPedidoActivo(null);
    setGuardadoPedido(null);
    cargarVenta(leerVenta(negocio.negocioId, fechaNegocio()));
    void soltarSaliente(foto.saliente, foto.vacio, foto.soltar);
  }, [negocio, fotoDelSaliente, cargarVenta, soltarSaliente]);

  const abrirPedido = useCallback(
    async (p: Pedido) => {
      if (!negocio || p.id === pedidoEnPantalla.current?.id) return;
      const soltar = await tomarPedido(p.id);
      if (!soltar) {
        setAviso(`${p.nombre ?? `Pedido ${p.numero}`} está abierto en otra pestaña de este equipo.`);
        return;
      }
      const foto = fotoDelSaliente();
      soltarPedido.current = soltar;
      const abierto = p.estado === 'cobrando' ? { ...p, estado: 'abierto' as const } : p;
      if (p.estado === 'cobrando') guardarPedido(abierto);
      setPedidoActivo(abierto);
      setGuardadoPedido(null);
      cargarVenta(abierto.venta);
      if (p.estado === 'cobrando') {
        setAviso('Este pedido se estaba cobrando: revisa Comprobantes antes de cobrarlo otra vez.');
      }
      void soltarSaliente(foto.saliente, foto.vacio, foto.soltar);
    },
    [negocio, fotoDelSaliente, cargarVenta, soltarSaliente]
  );

  /**
   * Crea un pedido y lo pone en pantalla. Desde MOSTRADOR se lleva lo que haya escaneado; desde otro pedido,
   * ese queda guardado y el nuevo nace vacío. La copia en disco va después de dejar la pantalla lista.
   */
  const crearNuevoPedido = useCallback(
    async (nombre: string | null) => {
      if (!negocio) return;
      const desdeMostrador = pedidoActivo === null && lineas.length > 0;
      const venta: VentaEnCurso = desdeMostrador
        ? { idVenta: idVenta ?? crypto.randomUUID(), lineas, cliente, cuentaId: cuenta?.id ?? null, pagos, descuento: descuentoVenta }
        : { idVenta: crypto.randomUUID(), lineas: [], cliente: null, cuentaId: null, pagos: null, descuento: null };
      const nuevo = crearPedido(negocio.negocioId, fechaNegocio(), nombre, venta, pedidos);
      if (!guardarPedido(nuevo)) {
        setCreandoPedido(false);
        setAviso('No se pudo guardar el pedido en este equipo. La venta sigue en pantalla.');
        return;
      }
      // Primero la pantalla, en un solo paso: lo que se escanee desde aquí ya cae en el pedido nuevo.
      const foto = fotoDelSaliente();
      if (desdeMostrador) olvidarVenta();
      setPedidos((ps) => [...ps, nuevo]);
      setPedidoActivo(nuevo);
      setGuardadoPedido(null);
      setCreandoPedido(false);
      cargarVenta(venta);
      await soltarSaliente(foto.saliente, foto.vacio, foto.soltar);
      soltarPedido.current = await tomarPedido(nuevo.id);
      pedirPersistencia();
      void guardarPedidoEnDisco(nuevo);
    },
    [negocio, pedidoActivo, lineas, idVenta, cliente, cuenta, pagos, descuentoVenta, pedidos, cargarVenta, fotoDelSaliente, soltarSaliente]
  );

  const intentarCerrar = useCallback(async () => {
    if (lineas.length === 0) {
      setAviso('No hay productos en el mostrador.');
      return;
    }
    if (!cuenta && !pagos) {
      setAviso('Elige en qué cuenta entra el dinero antes de cerrar.');
      return;
    }
    if (!negocio || abriendoConfirmacion.current) return;
    abriendoConfirmacion.current = true;
    setAviso('');
    // Se cobra con el precio de hoy, que es el que registra la base. Sin respuesta se cobra con el
    // cargado, como siempre (A5).
    const precios = await preciosVigentes(negocio.negocioId, lineas.map((l) => l.productoId)).catch(() => null);
    const cambios = precios
      ? lineas
          .filter((l) => precios.has(l.productoId) && precios.get(l.productoId) !== l.precioUnit)
          .map((l) => ({ nombre: l.nombre, antes: l.precioUnit, ahora: precios.get(l.productoId)! }))
      : [];
    const vigentes = cambios.length && precios
      ? lineas.map((l) => (precios.has(l.productoId) ? { ...l, precioUnit: precios.get(l.productoId)! } : l))
      : lineas;
    if (cambios.length && precios) {
      setLineas(vigentes);
      setProductos((ps) => ps?.map((p) => (precios.has(p.id) ? { ...p, precio: precios.get(p.id)! } : p)) ?? ps);
    }
    setPreciosCambiados(cambios);
    abriendoConfirmacion.current = false;
    // Un reparto hecho sobre otro total ya no suma: se vuelve a repartir antes de confirmar.
    const total = desglosarVenta(vigentes, descuentoVenta).total;
    if (pagos && pagos.reduce((suma, g) => suma + g.monto, 0) !== total) {
      setAvisoReparto(`El total cambió a ${formatearCOP(total)}: vuelve a repartir el pago`);
      setRepartiendo(true);
      return;
    }
    setConfirmando(true);
  }, [lineas, cuenta, pagos, negocio, descuentoVenta]);

  const confirmarVenta = useCallback(async () => {
    if (!negocio || (!cuenta && !pagos) || ocupado || !idVenta) return;
    setOcupado(true);
    // Antes de cobrar, los descuentos como están ahora: un manual editado o eliminado se quita, y un automático
    // que hoy no vale (un borrador o un pedido de otro día) se corrige. Sin conexión se usa la lista que había.
    const actuales = await listarDescuentosNegocio(negocio.negocioId).catch(() => descuentos);
    setDescuentos(actuales);
    const vivos = new Set(actuales.filter((d) => d.activo).map((d) => d.id));
    const caido = (d: { descuentoId: string; automatico?: boolean } | null | undefined) => !!d && !d.automatico && !vivos.has(d.descuentoId);
    const caidos = [...lineas.map((l) => l.descuento).filter(caido), ...(caido(descuentoVenta) ? [descuentoVenta] : [])];
    const ventaVigente = caido(descuentoVenta) ? null : descuentoVenta;
    const vigentes = aplicarPromociones(
      caidos.length > 0 ? lineas.map((l) => (caido(l.descuento) ? { ...l, descuento: null } : l)) : lineas,
      actuales.filter((d) => d.automatico && d.activo),
      categoriaDe,
      fechaNegocio(),
      ventaVigente !== null
    );
    if (vigentes !== lineas || ventaVigente !== descuentoVenta) {
      setLineas(vigentes);
      setDescuentoVenta(ventaVigente);
      setPagos(null);
      setConfirmando(false);
      setOcupado(false);
      setAviso(
        caidos.length > 0
          ? `${[...new Set(caidos.map((d) => d?.nombre))].join(', ')} cambió en Configuración: vuelve a aplicarlo.`
          : 'Los descuentos automáticos cambiaron: revisa el total antes de cobrar.'
      );
      return;
    }
    const cobrando = pedidoActivo ? { ...pedidoActivo, estado: 'cobrando' as const, guardadoEn: Date.now() } : null;
    if (cobrando) {
      guardarPedido(cobrando);
      await guardarPedidoEnDisco(cobrando);
    }
    try {
      const r = await registrarOEncolar({
        uuidCliente: idVenta,
        negocioId: negocio.negocioId,
        cuentaId: cuenta?.id ?? null,
        pagos,
        terceroId: cliente?.id ?? null,
        lineas,
        descuento: descuentoVenta,
        pedidoNumero: pedidoActivo?.numero ?? null,
        fecha: fechaNegocio(),
        hora: horaNegocio(),
      });
      setResultado(
        r
          ? { tipo: 'ok', consecutivo: r.consecutivo, total: r.total, ventaId: r.ventaId }
          : { tipo: 'pendiente', total: totales.total }
      );
      setConfirmando(false);
      if (pedidoActivo) {
        await borrarPedido(negocio.negocioId, pedidoActivo.id);
        setPedidos((ps) => ps.filter((p) => p.id !== pedidoActivo.id));
        soltarPedido.current?.();
        soltarPedido.current = null;
        setPedidoActivo(null);
        setGuardadoPedido(null);
        // MOSTRADOR vuelve con la venta suelta que tenía.
        const suelta = leerVenta(negocio.negocioId, fechaNegocio());
        setLineas(suelta?.lineas ?? []);
        setIdVenta(suelta?.idVenta ?? null);
        setCliente(suelta?.cliente ?? null);
        setPagos(suelta?.pagos ?? null);
        setDescuentoVenta(suelta?.descuento ?? null);
        setCuenta(suelta?.cuentaId ? (cuentas.find((c) => c.id === suelta.cuentaId) ?? null) : null);
      } else {
        setLineas([]);
        setIdVenta(null);
        setCuenta(null);
        setPagos(null);
        setCliente(null);
        setDescuentoVenta(null);
        olvidarVenta();
      }
      setRecuperada(false);
      setPreciosCambiados([]);
    } catch (error) {
      if (cobrando) guardarPedido({ ...cobrando, estado: 'abierto', guardadoEn: Date.now() });
      setConfirmando(false);
      setAviso(error instanceof Error ? error.message : 'No se pudo registrar la venta.');
      // El rastro lleva el identificador de la venta, así que se puede seguir de aquí a `ventas`.
      void rastrearFallo(error, { pantalla: 'mostrador', correlacion: idVenta ?? undefined });
    } finally {
      setOcupado(false);
      scan.current?.enfocar();
    }
  }, [negocio, cuenta, pagos, cliente, lineas, descuentoVenta, totales.total, ocupado, idVenta, pedidoActivo, cuentas, descuentos, categoriaDe]);

  useEffect(() => {
    function atajos(e: KeyboardEvent) {
      if (e.key === 'F9') {
        e.preventDefault();
        if (confirmando) void confirmarVenta();
        else void intentarCerrar();
      }
      if (e.key === 'F8' && puedeDescontarVenta && !hayDialogo && lineas.length > 0) {
        e.preventDefault();
        pedirDescuentoVenta();
      }
      if (e.key === 'Escape' && !e.defaultPrevented && !hayDialogo && !pedidoActivo) {
        escapes.current += 1;
        setTimeout(() => (escapes.current = 0), 600);
        if (escapes.current >= 2 && lineas.length > 0) limpiar();
      }
    }
    window.addEventListener('keydown', atajos);
    return () => window.removeEventListener('keydown', atajos);
  }, [confirmando, confirmarVenta, hayDialogo, intentarCerrar, limpiar, lineas.length, pedidoActivo, pedirDescuentoVenta, puedeDescontarVenta]);

  if (!negocio) return null;
  if (productos === null) return <Esqueleto lineas={6} />;

  // Un reparto armado sobre el total anterior ya no suma: cambiar un descuento pide elegir la cuenta otra vez.
  const aplicarDescuentoProducto = (sku: string, d: DescuentoDeLinea | null) => {
    if (d) setLineas((ls) => ls.map((l) => (l.sku === sku ? { ...l, descuento: d } : l)));
    else quitarDescuentoProducto(sku);
    setPagos(null);
    setSkuDescuento(null);
  };

  // La promoción que el cajero quita no vuelve sola a esa línea; un descuento manual solo se quita.
  const quitarDescuentoProducto = (sku: string) => {
    setLineas((ls) =>
      ls.map((l) =>
        l.sku === sku
          ? {
              ...l,
              descuento: null,
              promocionQuitada: l.descuento?.automatico ? [...(l.promocionQuitada ?? []), l.descuento.descuentoId] : l.promocionQuitada,
            }
          : l
      )
    );
    setPagos(null);
  };

  // Lo que se paga en efectivo: el total si la cuenta lo es, y solo esa porción en un pago mixto.
  const efectivoDeLaVenta = pagos
    ? pagos
        .filter((g) => cuentas.find((c) => c.id === g.cuentaId)?.esEfectivo)
        .reduce((suma, g) => suma + g.monto, 0)
    : cuenta?.esEfectivo
      ? totales.total
      : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* La confirmación para un lector de pantalla: la región existe desde el montaje y recibe el texto
          después, que es como se anuncia. Fuera del flujo, no mueve nada ni toma el foco. */}
      <p role="status" className="sr-only" data-e2e="venta-anuncio">
        {resultado?.tipo === 'ok'
          ? `Comprobante ${resultado.consecutivo} por ${formatearCOP(resultado.total)}.`
          : resultado
            ? `Venta por ${formatearCOP(resultado.total)} guardada en este equipo.`
            : ''}
      </p>
      <MensajeError caja e2e="aviso">{aviso}</MensajeError>
      <ErrorDeLectura que="los descuentos del negocio" error={errorDescuentos} onReintentar={() => { cargarDescuentos(); scan.current?.enfocar(); }} e2e="descuentos-error" />
      <ErrorDeLectura que="los impuestos del negocio" error={errorImpuestos} onReintentar={() => { cargarImpuestos(); scan.current?.enfocar(); }} e2e="impuestos-error" />
      <ErrorDeLectura que="los pedidos guardados en este equipo" error={errorPedidos} onReintentar={() => { cargarPedidos(); scan.current?.enfocar(); }} e2e="pedidos-error" />
      {recuperada && lineas.length > 0 && (
        <p className="text-muted text-contenido" data-e2e="venta-recuperada">
          Recuperamos la venta que estabas armando en este equipo.
        </p>
      )}
      {resultado && (
        <p className="text-success bg-success/10 rounded-control px-3 py-2 font-bold" data-e2e="venta-ok">
          {resultado.tipo === 'ok' ? (
            <>
              Comprobante {resultado.consecutivo} por {formatearCOP(resultado.total)}.{' '}
              <Link className="underline" to={`/comprobantes/${resultado.ventaId}`}>
                Ver e imprimir
              </Link>
            </>
          ) : (
            // `registrarOEncolar` devuelve `null` cuando la venta quedó en la cola: sin red, o
            // con un fallo que el clasificador considera reintentable.
            <>
              Venta por {formatearCOP(resultado.total)} guardada en este equipo. Se registra sola en
              cuanto vuelva la conexión.
            </>
          )}
        </p>
      )}

      <FranjaPedidos
        pedidos={pedidos}
        activo={pedidoActivo}
        hoy={fechaNegocio()}
        guardado={guardadoPedido}
        onMostrador={volverAMostrador}
        onAbrir={(p) => void abrirPedido(p)}
        onNuevo={() => setCreandoPedido(true)}
        onCerrarLista={() => scan.current?.enfocar()}
      />

      <Tarjeta>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-muted text-contenido font-bold">1 · ¿Quién compra?</span>
          <span className="font-semibold" data-e2e="cliente-actual">
            {cliente?.nombre ?? 'Consumidor final'}
          </span>
          <span className="num text-muted text-contenido">
            ({cliente?.numeroDocumento ?? CONSUMIDOR_FINAL.numeroDocumento})
          </span>
          <Boton variante="contorno" onClick={() => setEligiendoCliente(true)} data-e2e="cambiar-cliente">
            Cambiar
          </Boton>
        </div>
      </Tarjeta>

      {/* Los botones rápidos son un panel hermano, fuera de la tarjeta del paso 2:
          son un atajo al catálogo, no parte del registro de la venta. */}
      <div className="flex flex-col gap-4 lg:flex-row">
        <Tarjeta className="min-w-0 flex-1">
          <div className="flex flex-col gap-3">
            {/* La entrada va al frente de la etiqueta y se queda con el ancho que sobre; en pantalla
                angosta el wrap las apila. La lista es una tabla, así que baja a su propia fila. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="text-muted text-contenido font-bold">2 · ¿Qué compra?</p>
              <div className="min-w-0 flex-1">
                <EntradaEscaneo ref={scan} onCodigo={alCodigo} pausado={confirmando} />
              </div>
            </div>
            <PanelLineas
              lineas={lineas}
              desglose={desgloseLineas}
              mostrarDescuentos={mostrarDescuento}
              mostrarImpuestos={mostrarImpuestos}
              onCantidad={(sku, cantidad) => setLineas((ls) => ls.map((l) => (l.sku === sku ? { ...l, cantidad } : l)))}
              onQuitar={(sku) => setLineas((ls) => ls.filter((l) => l.sku !== sku))}
              onLectura={(codigo, terminado) => {
                if (!terminado) {
                  scan.current?.recibir(codigo);
                  return;
                }
                scan.current?.enfocar();
                alCodigo(codigo);
              }}
              onDescuento={descuentosDeProducto.length > 0 ? setSkuDescuento : undefined}
              onQuitarDescuento={quitarDescuentoProducto}
              descuentoBloqueado={descuentoVenta ? 'Hay un descuento a la venta: quítalo para descontar un producto.' : null}
            />
          </div>
        </Tarjeta>
        <BotonesRapidos productos={productos} onElegir={agregarProducto} />
      </div>

      <Tarjeta>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="text-muted text-contenido font-bold">3 · ¿En qué cuenta entra el dinero?</p>
          {errorCuentas ? (
            <ErrorDeLectura
              que="las cuentas del negocio"
              error={errorCuentas}
              onReintentar={() => {
                cargarCuentas();
                scan.current?.enfocar();
              }}
              e2e="cuentas-error"
            />
          ) : cuentas.length === 0 ? (
            <span className="text-muted text-contenido">
              Configura al menos una cuenta en Configuración para poder cobrar.
            </span>
          ) : pagos ? (
            <>
              <span className="text-contenido font-semibold" data-e2e="pago-mixto-resumen">
                {resumenPagos}
              </span>
              <Boton variante="contorno" onClick={() => setRepartiendo(true)} data-e2e="quitar-pago-mixto">
                Cambiar
              </Boton>
            </>
          ) : (
            <>
              <GrupoOpciones
                etiqueta="Cuenta de pago"
                valor={cuenta?.id ?? null}
                onElegir={(id) => setCuenta(cuentas.find((c) => c.id === id) ?? null)}
                opciones={cuentas.map((c) => ({
                  valor: c.id,
                  texto: c.nombre,
                  e2e: `cuenta-${c.nombre.toLowerCase().replace(/\s+/g, '-')}`,
                }))}
              />
              {cuentas.length > 1 && (
                <Boton
                  variante="contorno"
                  onClick={() => setRepartiendo(true)}
                  disabled={lineas.length === 0}
                  data-e2e="abrir-pago-mixto"
                >
                  Pago mixto
                </Boton>
              )}
            </>
          )}
          {efectivoDeLaVenta > 0 && (
            <Boton variante="contorno" onClick={() => setCalculandoCambio(true)} data-e2e="abrir-cambio">
              Cambio
            </Boton>
          )}
        </div>
      </Tarjeta>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="divide-border flex items-end divide-x" data-e2e="total-desglose">
          <div className="pr-4">
            <span className="text-muted block text-contenido font-bold uppercase">Subtotal</span>
            <span className="num text-cifra font-extrabold" data-e2e="subtotal">
              {formatearCOP(totales.bruto)}
            </span>
          </div>
          {mostrarDescuento && (
            <div className="px-4">
              <span className="text-muted block text-contenido font-bold uppercase">{etiquetaDescuento}</span>
              <span className="num text-destructive text-cifra font-extrabold" data-e2e="descuento-total">
                −{formatearCOP(totales.descuentoLineas + totales.descuentoVenta)}
              </span>
              {nombresDescuento.length > 1 && (
                <span className="text-muted block max-w-40 text-etiqueta leading-tight" data-e2e="detalle-descuentos">
                  {nombresDescuento.join(' · ')}
                </span>
              )}
            </div>
          )}
          {mostrarImpuestos && (
            <div className="px-4">
              <span className="text-muted block text-contenido font-bold uppercase">Impuestos</span>
              <span className="num text-cifra font-extrabold" data-e2e="impuestos-total">
                +{formatearCOP(totales.impuesto)}
              </span>
              {impuestosPorNombre.size > 1 && (
                <span className="num text-muted block max-w-40 text-etiqueta leading-tight" data-e2e="detalle-impuestos">
                  {[...impuestosPorNombre].map(([nombre, monto]) => `${nombre} ${formatearCOP(monto)}`).join(' · ')}
                </span>
              )}
            </div>
          )}
          <div className="pl-4">
            <span className="text-muted block text-contenido font-bold uppercase">Total</span>
            {/* La clave cambia con el total: el destello se repite en cada cambio. */}
            <span key={totales.total} className="num text-success destello-total -mx-1 px-1 text-total font-extrabold" data-e2e="total">
              {formatearCOP(totales.total)}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Boton variante="contorno" onClick={limpiar} disabled={lineas.length === 0 || pedidoActivo !== null}>
              Limpiar (Esc, Esc)
            </Boton>
            {puedeDescontarVenta && (
              <Boton
                variante="contorno"
                onClick={pedirDescuentoVenta}
                disabled={lineas.length === 0}
                aria-disabled={hayDescuentoEnProductos}
                className={hayDescuentoEnProductos ? 'opacity-40' : ''}
                data-e2e="abrir-descuento-venta"
              >
                {descuentoVenta
                  ? `Descuento a la venta · ${descuentoVenta.tipo === 'pct' ? `${descuentoVenta.valor}%` : formatearCOP(descuentoVenta.valor)} (F8)`
                  : 'Descuento a la venta (F8)'}
              </Boton>
            )}
            <Boton grande onClick={() => void intentarCerrar()} disabled={lineas.length === 0} data-e2e="cerrar-venta">
              CERRAR VENTA (F9)
            </Boton>
          </div>
          {avisoVentaBloqueada && (
            <p className="text-destructive text-contenido font-semibold" role="status" data-e2e="descuento-venta-bloqueado">
              Hay descuentos en productos: quítalos para hacer descuento al total de la venta.
            </p>
          )}
        </div>
      </div>

      <Modal abierto={confirmando} titulo="Confirmar la venta" onCerrar={() => setConfirmando(false)}>
        {pedidoActivo && (
          <p className="mb-1 font-bold" data-e2e="confirmar-pedido">
            {pedidoActivo.nombre ? `Pedido ${pedidoActivo.numero}: ${pedidoActivo.nombre}` : `Pedido ${pedidoActivo.numero}`}
          </p>
        )}
        <p className="mb-1">
          {lineas.length} línea{lineas.length === 1 ? '' : 's'} ·{' '}
          {resumenPagos ? (
            <>
              Pago: <strong data-e2e="confirmar-reparto">{resumenPagos}</strong>
            </>
          ) : (
            <>
              Cuenta: <strong>{cuenta?.nombre}</strong>
            </>
          )}
          <br />
          Cliente: <strong>{cliente?.nombre ?? 'Consumidor final'}</strong>{' '}
          <span className="num">({cliente?.numeroDocumento ?? CONSUMIDOR_FINAL.numeroDocumento})</span>
        </p>
        {conDescuento && (
          <div className="bg-surface rounded-card num mb-2 space-y-1 p-3 text-contenido" data-e2e="confirmar-descuentos">
            <p className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatearCOP(totales.bruto)}</span>
            </p>
            {totales.descuentoLineas > 0 && (
              <p className="text-destructive flex justify-between">
                <span>Desc. productos</span>
                <span>−{formatearCOP(totales.descuentoLineas)}</span>
              </p>
            )}
            {totales.descuentoVenta > 0 && (
              <p className="text-destructive flex justify-between">
                <span>
                  Desc. venta{descuentoVenta?.tipo === 'pct' ? ` ${descuentoVenta.valor}%` : ''}
                  {descuentoVenta ? ` · ${descuentoVenta.nombre}` : ''}
                </span>
                <span>−{formatearCOP(totales.descuentoVenta)}</span>
              </p>
            )}
          </div>
        )}
        {preciosCambiados.length > 0 && (
          <div className="mb-2 space-y-1" data-e2e="confirmar-precios">
            {preciosCambiados.map((c) => (
              <p key={c.nombre} className="text-warning text-contenido font-semibold">
                ⚠ Cambió un precio: {c.nombre} {formatearCOP(c.antes)} → {formatearCOP(c.ahora)}
              </p>
            ))}
          </div>
        )}
        <p className="num mb-4 text-total font-extrabold">{formatearCOP(totales.total)}</p>
        <p className="text-muted mb-4 text-contenido">Una vez cerrado, el comprobante solo se puede anular (con motivo).</p>
        <FilaAcciones>
          <Boton variante="contorno" onClick={() => setConfirmando(false)} disabled={ocupado}>
            Volver
          </Boton>
          <Boton onClick={() => void confirmarVenta()} disabled={ocupado} data-e2e="confirmar-venta">
            {ocupado ? 'Registrando…' : 'Confirmar (F9)'}
          </Boton>
        </FilaAcciones>
      </Modal>

      <Cambio
        abierto={calculandoCambio}
        aPagar={efectivoDeLaVenta}
        onCerrar={() => setCalculandoCambio(false)}
      />

      <PagoMixto
        abierto={repartiendo}
        total={totales.total}
        cuentas={cuentas}
        inicial={pagos}
        aviso={avisoReparto}
        onCerrar={() => {
          setRepartiendo(false);
          setAvisoReparto(null);
        }}
        onAceptar={(elegidos) => {
          // Un solo pago no es un reparto: es la cuenta de siempre, y el paso 3 la muestra elegida.
          const unico = elegidos.length === 1 ? (cuentas.find((c) => c.id === elegidos[0]!.cuentaId) ?? null) : null;
          setPagos(unico ? null : elegidos);
          setCuenta(unico);
          setRepartiendo(false);
          setAvisoReparto(null);
          setAviso('');
        }}
      />

      <NuevoPedido
        abierto={creandoPedido}
        numero={siguienteNumero(pedidos, fechaNegocio())}
        llevaMostrador={pedidoActivo === null && lineas.length > 0}
        onGuardar={(nombre) => void crearNuevoPedido(nombre)}
        onCerrar={() => setCreandoPedido(false)}
      />

      <SelectorCliente
        negocioId={negocio.negocioId}
        abierto={eligiendoCliente}
        onCerrar={() => setEligiendoCliente(false)}
        onElegir={(c) => {
          setCliente(c);
          setEligiendoCliente(false);
        }}
      />

      <DescuentoProducto
        sku={skuDescuento}
        lineas={lineas}
        descuentos={descuentosDeProducto}
        mostrarImpuesto={mostrarImpuestos}
        onAplicar={aplicarDescuentoProducto}
        onCerrar={() => setSkuDescuento(null)}
      />
      <DescuentoVenta
        abierto={descontandoVenta}
        lineas={lineas}
        descuento={descuentoVenta}
        descuentos={descuentosDeVenta}
        onAplicar={(d) => {
          setDescuentoVenta(d);
          setPagos(null);
          setDescontandoVenta(false);
        }}
        onCerrar={() => setDescontandoVenta(false)}
      />
    </div>
  );
}
